/**
 * Single source of truth for score tiers.
 *
 * These MUST match the backend, which is the only thing that actually assigns a
 * tier: see backend/scoring.py `compute_full_score` — Pioneer >= 750,
 * Builder >= 600, Starter below that. There are exactly three.
 *
 * History: the UI used to carry a 7-band map (Trailblazer / Advancer / Rising /
 * Seedling) that the backend never emitted, while 12 other files carried their
 * own 3-band maps with drifting colours. Everything now imports from here.
 */

export const SCORE_MIN = 300;
export const SCORE_MAX = 900;

export const TIERS = [
    {
        name: "Pioneer",
        min: 750,
        max: 900,
        color: "#10b981",
        text: "text-green-pioneer",
        bg: "bg-green-pioneer/15",
        border: "border-green-pioneer/30",
        ring: "ring-green-pioneer/25",
        glow: "rgba(16,185,129,0.4)",
        shadow: "shadow-[0_0_30px_rgba(16,185,129,0.4)]",
        blurb: "Strong, well-evidenced profile",
        lenderNote: "Preferred — broad product eligibility",
    },
    {
        name: "Builder",
        min: 600,
        max: 749,
        color: "#f59e0b",
        text: "text-amber-builder",
        bg: "bg-amber-builder/15",
        border: "border-amber-builder/30",
        ring: "ring-amber-builder/25",
        glow: "rgba(245,158,11,0.4)",
        shadow: "shadow-[0_0_30px_rgba(245,158,11,0.3)]",
        blurb: "Solid foundation, still building depth",
        lenderNote: "Standard — most entry products in reach",
    },
    {
        name: "Starter",
        min: 300,
        max: 599,
        color: "#ef4444",
        text: "text-red-starter",
        bg: "bg-red-starter/15",
        border: "border-red-starter/30",
        ring: "ring-red-starter/25",
        glow: "rgba(239,68,68,0.4)",
        shadow: "shadow-[0_0_30px_rgba(239,68,68,0.3)]",
        blurb: "Early profile — more signals will help",
        lenderNote: "Build-first — secured and starter products",
    },
];

export const TIER_ORDER = TIERS.map((t) => t.name);

/** { Pioneer: "#10b981", ... } — for recharts fills and inline styles. */
export const TIER_COLORS = Object.fromEntries(TIERS.map((t) => [t.name, t.color]));

const BY_NAME = Object.fromEntries(TIERS.map((t) => [t.name, t]));
const FALLBACK = BY_NAME.Starter;

/** Clamp a raw number into the valid score range. */
export function clampScore(score) {
    const n = Number(score);
    if (!Number.isFinite(n)) return SCORE_MIN;
    return Math.max(SCORE_MIN, Math.min(SCORE_MAX, n));
}

/** Tier name for a numeric score. Mirrors backend/scoring.py. */
export function getTier(score) {
    const n = clampScore(score);
    const hit = TIERS.find((t) => n >= t.min);
    return (hit || FALLBACK).name;
}

/**
 * Full tier descriptor by name. Unknown names fall back to Starter so the UI
 * never renders `undefined` colours; in development we warn, because an unknown
 * name means the backend started emitting bands this file does not know about.
 */
export function tierMeta(name) {
    const hit = BY_NAME[name];
    if (hit) return hit;
    if (process.env.NODE_ENV === "development" && name) {
        // eslint-disable-next-line no-console
        console.warn(`[tiers] Unknown tier "${name}" — falling back to Starter. Update src/lib/tiers.js to match backend/scoring.py.`);
    }
    return FALLBACK;
}

/** Convenience: descriptor straight from a score. */
export function tierMetaForScore(score) {
    return tierMeta(getTier(score));
}

/**
 * Indicative credit limit in rupees. Illustrative only — this is not an offer
 * and no lender is bound by it. Ladder preserved from the original
 * LenderSimulator implementation so simulator output does not change.
 */
export function indicativeLimit(score) {
    const n = clampScore(score);
    if (n >= 800) return 100000;
    if (n >= 750) return 75000;
    if (n >= 700) return 50000;
    if (n >= 650) return 35000;
    if (n >= 600) return 25000;
    if (n >= 500) return 15000;
    return 5000;
}
